import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialsCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  // Auto-play every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying || testimonials.length <= 1) return;
    
    const interval = setInterval(nextSlide, 15000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, testimonials.length]);

  // Touch handlers for mobile swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-white to-[#F9F9F8]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3">
            O que dizem sobre nós
          </h2>
          <p className="text-[#4B4C52] max-w-2xl mx-auto">
            Pesquisadores de todo o Brasil compartilham suas experiências com o Quero Publicar
          </p>
        </div>

        <div 
          className="relative"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Carousel Container */}
          <div 
            className="overflow-hidden rounded-2xl"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.testimonial_id || index}
                  className="w-full flex-shrink-0 px-4"
                >
                  <div className="bg-white rounded-2xl shadow-lg border border-[#E4E4E7] p-8 sm:p-10 relative overflow-hidden">
                    {/* Quote decoration */}
                    <div className="absolute top-6 right-6 opacity-10">
                      <Quote className="h-24 w-24 text-[#002FA7]" />
                    </div>
                    
                    {/* Testimonial content */}
                    <div className="relative z-10">
                      {/* Quote text */}
                      <blockquote className="text-lg sm:text-xl text-[#0A0A0A] leading-relaxed mb-8 italic">
                        "{testimonial.content}"
                      </blockquote>
                      
                      {/* Author info */}
                      <div className="flex items-center gap-4">
                        {/* Photo */}
                        {testimonial.photo_url ? (
                          <img
                            src={testimonial.photo_url}
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover border-3 border-[#002FA7]/20 shadow-md"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#002FA7] to-[#0040E0] text-white flex items-center justify-center text-xl font-bold shadow-md">
                            {testimonial.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        
                        {/* Name and title */}
                        <div>
                          <h4 className="font-bold text-lg text-[#0A0A0A]">
                            {testimonial.name}
                          </h4>
                          <p className="text-[#002FA7] font-medium text-sm">
                            {testimonial.title}
                          </p>
                          {testimonial.institution && (
                            <p className="text-[#4B4C52] text-sm">
                              {testimonial.institution}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Decorative gradient */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#002FA7] to-[#0040E0]"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation arrows */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 bg-white rounded-full p-2 sm:p-3 shadow-lg border border-[#E4E4E7] hover:bg-[#F9F9F8] transition-colors z-10"
                aria-label="Depoimento anterior"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-[#002FA7]" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 bg-white rounded-full p-2 sm:p-3 shadow-lg border border-[#E4E4E7] hover:bg-[#F9F9F8] transition-colors z-10"
                aria-label="Próximo depoimento"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-[#002FA7]" />
              </button>
            </>
          )}

          {/* Dots indicator */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-[#002FA7] w-8' 
                      : 'bg-[#E4E4E7] w-2 hover:bg-[#002FA7]/50'
                  }`}
                  aria-label={`Ir para depoimento ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
